import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import {
    Button,
    Spinner,
    Modal,
    Form,
    OverlayTrigger,
    Tooltip,
    Row,
    Col,
    Tab,
    Tabs
} from "react-bootstrap";
import {
    FiEdit2,
    FiTrash2,
    FiDownload,
    FiFileText,
    FiFile,
    FiRefreshCw,
    FiPlus,
    FiLink,
    FiUpload,
    FiSearch
} from "react-icons/fi";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

const initialForm = {
    sku: "",
    product_name: "",
    category: "",
    color: "",
    capacity: "",
    size_and_weight: "",
    display: "",
    product_description: "",
    price: "",
    sales_price: "",
    discount_price: "",
    margin_price: "",
    quantity: "",
    status: "Active",
    product_img: ""
};

const categoryOptions = [
    { value: "Smartphones", label: "Smartphones" },
    { value: "Laptops", label: "Laptops" },
    { value: "Accessories", label: "Accessories" }
];

const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" }
];

const Products = () => {
    const [products, setProducts] = useState([]);
    const [usedProductIds, setUsedProductIds] = useState([]);
    const [pending, setPending] = useState(true);
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const fileInputRef = useRef(null);
    const [activeTab, setActiveTab] = useState("upload");
    const [userRole, setUserRole] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();
    const fetchingRef = useRef(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const role = localStorage.getItem("user_role");
        setUserRole(role || "");
        fetchData();
    }, []);

    const hasEditPermission = ["admin", "supervisor", "editor", "superadmin"].includes(userRole);

    const fetchData = async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        try {
            setPending(true);

            const [productsRes, usedProductIdsRes] = await Promise.all([
                axios.get("https://www.azzenterprises.com/api/products", {
                    withCredentials: true
                }),
                axios.get("https://www.azzenterprises.com/api/customers/product-ids")
            ]);

            setProducts(productsRes.data);

            const productIdsAsNumbers = usedProductIdsRes.data.productIds.map(id => parseInt(id));
            setUsedProductIds(productIdsAsNumbers);

        } catch (err) {
            const serverMsg = err.response?.data?.error || err.message;
            setError(serverMsg);
            if (serverMsg === "Token required") {
                if (!toast.isActive("token-expired")) {
                    toast.error("Session expired. Redirecting to login...", {
                        toastId: "token-expired"
                    });
                }
                setTimeout(() => navigate("/login"), 1300);
            } else {

                console.error("Failed to fetch used product IDs:", err);
                try {
                    const productsRes = await axios.get("https://www.azzenterprises.com/api/products", {
                        withCredentials: true
                    });
                    setProducts(productsRes.data);
                    setUsedProductIds([]);
                } catch (productsErr) {
                    console.error("Failed to fetch products:", productsErr);
                }
            }
        } finally {
            setPending(false);
            fetchingRef.current = false;
        }
    };

    // Helper function to check if product is in use
    const isProductInUse = (productId) => {
        return usedProductIds.includes(parseInt(productId));
    };

    const confirmDelete = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirmed = async () => {
        try {
            await axios.delete(`https://www.azzenterprises.com/api/products/${deleteId}`, {
                withCredentials: true
            });
            fetchData();
            toast.success("Product deleted successfully");
        } catch {
            toast.error("Failed to delete product");
        } finally {
            setShowDeleteModal(false);
            setDeleteId(null);
        }
    };

    const handleExportExcel = () => {
        const ws = XLSX.utils.json_to_sheet(products);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Products");
        XLSX.writeFile(wb, "products.xlsx");
        toast.success("Exported to Excel");
    };

    const handleExportCSV = () => {
        const ws = XLSX.utils.json_to_sheet(products);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Products");
        XLSX.writeFile(wb, "products.csv");
        toast.success("Exported to CSV");
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Product List", 14, 10);
        autoTable(doc, {
            head: [["ID", "Name", "Category", "Price", "Qty", "Status"]],
            body: products.map((p) => [
                p.product_id,
                p.product_name,
                p.category || "-",
                `${p.price || 0}`,
                p.quantity || 0,
                p.status || "-"
            ]),
            styles: { fontSize: 8 }
        });
        doc.save("products.pdf");
        toast.success("Exported to PDF");
    };

    const formatPrice = (price) =>
        new Intl.NumberFormat("en-SG", {
            style: "currency",
            currency: "SGD"
        }).format(price);

    const openModal = (product = null) => {
        if (!hasEditPermission) return;
        if (product) {
            setEditingId(product.product_id);
            setFormData({ ...product });
            setImagePreview(product.product_img || "");
            setActiveTab(product.product_img?.startsWith("http") ? "url" : "upload");
        } else {
            setEditingId(null);
            setFormData(initialForm);
            setImagePreview("");
            setActiveTab("upload");
        }
        setImageFile(null);
        setShowModal(true);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error("File size exceeds 2MB limit");
            return;
        }

        const localPreview = URL.createObjectURL(file);
        setImageFile(file);
        setImagePreview(localPreview);
    };

    const handleImageUrlChange = (e) => {
        const url = e.target.value;
        setImagePreview(url);
        setFormData((prev) => ({ ...prev, product_img: url }));
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "number" ? Number(value) || 0 : value
        }));
    };

    const handleSubmit = async () => {
        if (!formData.product_name || !formData.price || !formData.quantity) {
            toast.error("Product name, price, and quantity are required");
            return;
        }

        try {
            setSubmitting(true);

            let imageUrl = formData.product_img;

            if (imageFile) {
                const formDataObj = new FormData();
                formDataObj.append("file", imageFile);

                const uploadRes = await axios.post(
                    "https://www.azzenterprises.com/api/aws/upload",
                    formDataObj,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                        withCredentials: true,
                    }
                );

                imageUrl = uploadRes.data.url;
                toast.update("uploading", {
                    render: "Image uploaded successfully",
                    type: "success",
                    autoClose: 1200,
                });
            }

            const endpoint = editingId
                ? `https://www.azzenterprises.com/api/products/${editingId}`
                : `https://www.azzenterprises.com/api/products/addproduct`;
            const method = editingId ? "put" : "post";

            await axios[method](
                endpoint,
                { ...formData, product_img: imageUrl },
                { withCredentials: true }
            );

            toast.success(editingId ? "Product updated successfully" : "Product added successfully");
            setShowModal(false);
            fetchData();

            setFormData(initialForm);
            setImageFile(null);
            setImagePreview("");
            setEditingId(null);
        } catch (err) {
            console.error("Error saving product:", err);
            toast.error("Failed to save product");
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        { name: "ID", selector: (row) => row.product_id, sortable: true, width: "70px" },
        { name: "SKU", selector: (row) => row.sku || "-", width: "90px" },
        { name: "Name", selector: (row) => row.product_name, sortable: true, width: "150px" },
        { name: "Category", selector: (row) => row.category || "-", width: "120px" },
        { name: "Color", selector: (row) => row.color || "-", width: "100px" },
        { name: "Capacity", selector: (row) => row.capacity || "-", width: "100px" },
        { name: "Size/Weight", selector: (row) => row.size_and_weight || "-", width: "120px" },
        { name: "Display", selector: (row) => row.display || "-", width: "100px" },
        { name: "Price", selector: (row) => formatPrice(row.price || 0), sortable: true, width: "100px" },
        { name: "Sale Price", selector: (row) => formatPrice(row.sales_price || 0), width: "100px" },
        { name: "Discount", selector: (row) => formatPrice(row.discount_price || 0), width: "100px" },
        { name: "Margin", selector: (row) => formatPrice(row.margin_price || 0), width: "100px" },
        { name: "Qty", selector: (row) => row.quantity || 0, sortable: true, width: "80px" },
        { name: "Status", selector: (row) => row.status || "-", sortable: true, width: "100px" },
        {
            name: "Image",
            cell: (row) =>
                row.product_img ? (
                    <img
                        src={row.product_img}
                        alt="product"
                        width={40}
                        height={40}
                        style={{ objectFit: "cover", borderRadius: "6px" }}
                    />
                ) : null,
            width: "80px"
        },
        ...(hasEditPermission
            ? [
                {
                    name: "Actions",
                    cell: (row) => {
                        const productInUse = isProductInUse(row.product_id);
                        return (
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => openModal(row)}
                                >
                                    <FiEdit2 />
                                </Button>

                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip>
                                            {productInUse
                                                ? "Product is in use by customers - cannot delete"
                                                : "Delete product"
                                            }
                                        </Tooltip>
                                    }
                                >
                                    <span>
                                        <Button
                                            variant={productInUse ? "outline-secondary" : "outline-danger"}
                                            size="sm"
                                            onClick={() => !productInUse && confirmDelete(row.product_id)}
                                            disabled={productInUse}
                                            style={{
                                                opacity: productInUse ? 0.5 : 1
                                            }}
                                        >
                                            <FiTrash2 />
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                            </div>
                        );
                    },
                    width: "120px",
                    ignoreRowClick: true
                }
            ]
            : [])
    ];

    const filteredProducts = products.filter(
        (p) =>
            (p.product_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.sku || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="card shadow-sm">
            <ToastContainer position="top-right" autoClose={1500} />
            <div className="card-body">
                {pending ? (
                    <div className="text-center my-5">
                        <Spinner animation="border" />
                    </div>
                ) : filteredProducts.length > 0 ? (
                    <>
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
                            <div className="input-group" style={{ maxWidth: "300px" }}>
                                <span className="input-group-text">
                                    <FiSearch />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name or SKU"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {hasEditPermission && (
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button variant="outline-primary" size="sm" onClick={() => openModal()}>
                                        <FiPlus className="me-1" /> Add Product
                                    </Button>
                                    <Button variant="outline-success" size="sm" onClick={handleExportCSV}>
                                        <FiFileText className="me-1" /> CSV
                                    </Button>
                                    <Button variant="outline-success" size="sm" onClick={handleExportExcel}>
                                        <FiFile className="me-1" /> Excel
                                    </Button>
                                    <Button variant="outline-success" size="sm" onClick={handleExportPDF}>
                                        <FiDownload className="me-1" /> PDF
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" onClick={fetchData}>
                                        <FiRefreshCw className="me-1" /> Refresh
                                    </Button>
                                </div>
                            )}
                        </div>

                        <DataTable
                            keyField="product_id"
                            columns={columns}
                            data={filteredProducts}
                            progressPending={pending}
                            pagination
                            responsive
                            highlightOnHover
                            striped
                            dense
                        />
                    </>
                ) : (
                    <div className="text-center my-5">
                        <p className="text-muted">No products found</p>
                        {hasEditPermission && (
                            <Button variant="outline-primary" onClick={() => openModal()}>
                                <FiPlus className="me-1" /> Add Product
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Rest of your modal code remains the same */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingId ? "Edit Product" : "Add Product"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>SKU</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="sku"
                                        value={formData.sku}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Product Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="product_name"
                                        value={formData.product_name}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Category</Form.Label>
                                    <Select
                                        options={categoryOptions}
                                        value={categoryOptions.find(
                                            (c) => c.value === formData.category
                                        )}
                                        onChange={(opt) =>
                                            setFormData((prev) => ({ ...prev, category: opt.value }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Color</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="color"
                                        value={formData.color}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Capacity</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="capacity"
                                        value={formData.capacity}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Size & Weight</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="size_and_weight"
                                        value={formData.size_and_weight}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Display</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="display"
                                        value={formData.display}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Status</Form.Label>
                                    <Select
                                        options={statusOptions}
                                        value={statusOptions.find(
                                            (opt) => opt.value === formData.status
                                        )}
                                        onChange={(opt) =>
                                            setFormData((prev) => ({ ...prev, status: opt.value }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                <Form.Group>
                                    <Form.Label>Product Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="product_description"
                                        value={formData.product_description}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Price</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Sale Price</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="sales_price"
                                        value={formData.sales_price}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Discount Price</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="discount_price"
                                        value={formData.discount_price}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Margin Price</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="margin_price"
                                        value={formData.margin_price}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Quantity</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="quantity"
                                        value={formData.quantity}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Tabs
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k)}
                            className="mb-3"
                        >
                            <Tab
                                eventKey="upload"
                                title={
                                    <>
                                        <FiUpload /> Upload
                                    </>
                                }
                            >
                                <Form.Group className="mb-3">
                                    <Form.Control
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        onChange={handleImageUpload}
                                    />
                                </Form.Group>
                            </Tab>
                            <Tab
                                eventKey="url"
                                title={
                                    <>
                                        <FiLink /> URL
                                    </>
                                }
                            >
                                <Form.Group>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter image URL"
                                        value={formData.product_img}
                                        onChange={handleImageUrlChange}
                                    />
                                </Form.Group>
                            </Tab>
                        </Tabs>
                        {imagePreview && (
                            <div className="text-center">
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="img-thumbnail"
                                    width="150"
                                />
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                {editingId ? "Updating..." : "Saving..."}
                            </>
                        ) : (
                            editingId ? "Update" : "Save"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this product?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirmed}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Products;